import { Component } from '@theme/component';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A custom element that renders a full-screen Swiper zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media container elements (one per slide, in order).
 * @property {HTMLElement} [swiperEl] - The Swiper container element.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media'];

  /** @type {any} */
  #swiper = null;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#swiper?.destroy(true, true);
    this.#swiper = null;
  }

  /**
   * Lazily creates the Swiper instance the first time the dialog is opened.
   * @returns {any}
   */
  #initSwiper() {
    if (this.#swiper) return this.#swiper;
    if (typeof Swiper === 'undefined' || !this.refs.swiperEl) return null;

    const { swiperEl } = this.refs;
    const loop = swiperEl.dataset.loop === 'true';

    this.#swiper = new Swiper(swiperEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      loop,
      keyboard: { enabled: true },
      navigation: {
        nextEl: this.querySelector('[data-sortabrick-zoom-next]'),
        prevEl: this.querySelector('[data-sortabrick-zoom-prev]'),
      },
      on: {
        slideChange: (swiper) => this.#handleSlideChange(swiper.realIndex),
      },
    });

    return this.#swiper;
  }

  /**
   * @param {number} index
   */
  #handleSlideChange(index) {
    this.#updateCounter(index);
    this.loadHighResolutionImage(this.refs.media[index]);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  /**
   * @param {number} index
   */
  #updateCounter(index) {
    const counter = this.querySelector('[data-sortabrick-zoom-counter]');
    if (!counter) return;

    const total = parseInt(counter.dataset.total, 10) || this.refs.media.length || 1;
    counter.textContent = `${index + 1} / ${total}`;
  }

  /**
   * Opens the zoom dialog at a given media index.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} [event] - The triggering event.
   */
  open(index, event) {
    event?.preventDefault();

    const { dialog } = this.refs;
    dialog.showModal();

    const swiper = this.#initSwiper();

    if (swiper) {
      if (swiper.params.loop) {
        swiper.slideToLoop(index, 0);
      } else {
        swiper.slideTo(index, 0);
      }
    }

    this.#updateCounter(index);
    this.loadHighResolutionImage(this.refs.media[index]);
  }

  /**
   * Closes the zoom dialog.
   */
  close() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Loads a high-resolution image for a specific media container, swapping it in once decoded.
   * @param {HTMLElement} [mediaContainer] - The media container element.
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer || !mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || image.dataset.highResLoaded === 'true') return false;

    const preloader = new Image();
    preloader.onload = () => {
      image.sizes = '';
      image.srcset = '';
      image.src = highResolutionUrl;
      image.dataset.highResLoaded = 'true';
    };
    preloader.src = highResolutionUrl;

    return true;
  }

  /**
   * Closes the dialog when the user presses the escape key.
   * Left/right arrow navigation is handled by Swiper's own keyboard module.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}